import React from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { MarketingLayout, StartServiceButton } from "@/components/marketing/MarketingLayout";
import { PageHero } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";

export default function Contact() {
  const origin = "https://artadmin.net";

  useSeo({
    title: "Contact AR Process Service | Memphis, TN",
    description:
      "Contact AR Process Service in Memphis, TN. Call (901) 695-2500 or email admin@artadmin.net for process serving and notary questions, quotes, and support.",
    canonicalPath: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "LegalService",
      "@id": "https://artadmin.net/#business",
      name: "AR Process Service and Notary",
      telephone: "+1-901-695-2500",
      email: "admin@artadmin.net",
      address: {
        "@type": "PostalAddress",
        streetAddress: "4730 Riverdale Rd",
        addressLocality: "Memphis",
        addressRegion: "TN",
        postalCode: "38141",
        addressCountry: "US",
      },
      url: `${origin}/contact`,
      openingHours: "Mo-Fr 08:00-18:00",
    },
  });

  return (
    <MarketingLayout>
      <PageHero
        overline="Contact"
        title="Contact AR Process Service"
        subtitle="Questions, quotes, support, or general inquiries — contact our Memphis team by phone or email. Ready to place a job? Use Start a Service to register."
      />

      <section className="px-5 py-14 sm:px-8 lg:py-20">
        <div className="mx-auto grid max-w-5xl gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Get in touch</h2>
            <div className="mt-6 space-y-4">
              <a
                href="https://www.google.com/maps?q=4730+Riverdale+Rd,+Memphis,+TN+38141"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-info-address"
                className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition hover:shadow-md"
              >
                <MapPin className="mt-0.5 h-6 w-6 shrink-0 text-sky-700" />
                <span>
                  <span className="block font-semibold text-slate-900">Office Address</span>
                  <span className="block text-[15px] text-slate-600">4730 Riverdale Rd, Memphis, TN 38141</span>
                </span>
              </a>
              <a
                href="tel:+19016952500"
                data-testid="contact-info-phone"
                className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition hover:shadow-md"
              >
                <Phone className="mt-0.5 h-6 w-6 shrink-0 text-sky-700" />
                <span>
                  <span className="block font-semibold text-slate-900">Phone</span>
                  <span className="block text-[15px] text-slate-600">(901) 695-2500</span>
                </span>
              </a>
              <a
                href="mailto:admin@artadmin.net"
                data-testid="contact-info-email"
                className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition hover:shadow-md"
              >
                <Mail className="mt-0.5 h-6 w-6 shrink-0 text-sky-700" />
                <span>
                  <span className="block font-semibold text-slate-900">Email</span>
                  <span className="block text-[15px] text-slate-600">admin@artadmin.net</span>
                </span>
              </a>
              <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
                <Clock className="mt-0.5 h-6 w-6 shrink-0 text-sky-700" />
                <span>
                  <span className="block font-semibold text-slate-900">Hours</span>
                  <span className="block text-[15px] text-slate-600">Monday – Friday, 8:00 AM – 6:00 PM CST</span>
                </span>
              </div>
            </div>
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-[15px] font-semibold text-slate-900">Ready to place a service order?</p>
              <p className="mt-1 text-sm text-slate-600">New clients can register to submit and track service requests.</p>
              <div className="mt-4">
                <StartServiceButton testid="contact-start-service-cta" />
              </div>
            </div>
          </div>

          <div>
            <div className="overflow-hidden rounded-2xl border border-slate-200">
              <iframe
                title="AR Process Service location map"
                src="https://www.google.com/maps?q=4730+Riverdale+Rd,+Memphis,+TN+38141&output=embed"
                className="h-full min-h-[420px] w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
